// ============ I Ching (易經) — the detailed reading ============
//
// The 64 Human Design gates ARE the 64 hexagrams, one to one, so this tab is
// never a generic reference book: every figure on screen is one the reader's
// own planets landed on, down to the individual line.
//
// What the knowledge base supplies (names, judgement, image, six line texts)
// is printed as written. Everything structural — trigram pair, nuclear /
// opposite / reversed figures, which line is central, correct or answered,
// the Jing Fang palace, and the hexagram the reading turns into once the
// reader's own line moves — is computed in `src/lib/iching.ts` and marked as
// calculated. That split is the same rule the BaZi tab was rebuilt on.

import React, { useEffect, useMemo, useState } from 'react';
import { useT, useLang } from '../lib/i18n';
import {
  buildIndex, assertIndex, buildPalaces, nuclearBits, oppositeBits, reversedBits,
  changedBits, allRoles, lineRole, lineName, splitMeaning, TRIGRAMS, trigramOf,
  type Hexagram, type HexIndex, type Bits, type LineRole, type Trigram,
} from '../lib/iching';

// ---------------------------------------------------------------- drawing

/**
 * A hexagram as it is actually written: six stacked lines, solid for yang,
 * split for yin, read from the bottom up. The Unicode glyph (䷀) is beautiful
 * but far too small to show *which* line is yours, and pointing at one line is
 * the entire purpose of this tab.
 */
function HexFigure({ bits, mark, size = 'md' }: { bits: Bits; mark?: number[]; size?: 'sm' | 'md' }) {
  return (
    <div className={`mm-hexfig ${size}`}>
      {/* Painted top line first, because CSS stacks downward while the
          classics count upward — line 6 is the one at the top of the page. */}
      {[5, 4, 3, 2, 1, 0].map(i => {
        const yang = !!bits[i];
        const on = mark?.includes(i + 1);
        return (
          <div key={i} className={`mm-hexline ${yang ? 'yang' : 'yin'} ${on ? 'on' : ''}`}>
            <i></i>{!yang && <i></i>}
          </div>
        );
      })}
    </div>
  );
}

function RoleChips({ role, en }: { role: LineRole; en: boolean }) {
  const chips: [string, string, string][] = []; // [key, label, tone]
  if (role.ruler) chips.push(['ruler', en ? '君位 ruler’s seat' : '君位 บัลลังก์ผู้นำ', 'good']);
  if (role.central) chips.push(['zhong', en ? '中 central' : '中 กลางตรีลักษณ์', 'good']);
  chips.push(['zheng', role.correct ? (en ? '當位 correct' : '當位 ถูกตำแหน่ง') : (en ? '不當位 out of place' : '不當位 ผิดตำแหน่ง'), role.correct ? 'good' : 'warn']);
  chips.push(['ying', role.answered ? (en ? `應 answered by ${role.partner}` : `應 มีเส้น ${role.partner} รับ`) : (en ? '無應 no answer' : '無應 ไร้เส้นรับ'), role.answered ? 'good' : 'warn']);
  if (role.riding) chips.push(['cheng', en ? '乘剛 riding a yang line' : '乘剛 หยินขี่หยาง (ฝืน)', 'warn']);
  if (role.carrying) chips.push(['cheng2', en ? '承陽 supporting the yang above' : '承陽 หยินหนุนหยาง (กลมกลืน)', 'good']);
  if (role.sole) chips.push(['sole', en ? 'the only line of its kind — pivotal' : 'เส้นเดี่ยวในดวง — เป็นแกนของทั้งกว้า', 'gold']);
  return (
    <div className="mm-ich-chips">
      {chips.map(([k, label, tone]) => <span key={k} className={`mm-ich-chip ${tone}`}>{label}</span>)}
    </div>
  );
}

function TrigramCard({ t, place, en }: { t: Trigram; place: string; en: boolean }) {
  return (
    <div className="mm-ich-tricard">
      <span className="mm-ich-triglyph">{t.glyph}</span>
      <div>
        <b>{t.cn} {t.pinyin} <span className="dim">· {en ? t.en : t.th}</span></b>
        <small className="dim">{place} · {en ? t.attr : t.attrTh} · {en ? t.element : t.elementTh} · {en ? t.family : t.familyTh}</small>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- the detail

function HexDetail({ ix, palaces, n, marked, onGo, en, t }: {
  ix: HexIndex; palaces: Map<number, any>; n: number; marked: number[];
  onGo: (n: number) => void; en: boolean; t: (th: string, e: string) => string;
}) {
  const h = ix.byNum.get(n)!;
  const bits = ix.bits(n);
  const m = splitMeaning(h.meaning);
  const roles = allRoles(bits);
  const pal = palaces.get(n);
  const rel: [string, string, number][] = [
    [t('互卦 แก่นใน', '互 Nuclear'), t('เครื่องยนต์ที่ซ่อนอยู่กลางเรื่อง (เส้น 2-5)', 'the engine hidden in lines 2-5'), ix.numOf(nuclearBits(bits))],
    [t('錯卦 ด้านกลับ', '錯 Opposite'), t('เรื่องเดียวกันเมื่อพลิกทุกเส้น', 'the same situation with every line flipped'), ix.numOf(oppositeBits(bits))],
    [t('綜卦 มุมคู่กรณี', '綜 Reversed'), t('ภาพเดียวกันเมื่อมองจากอีกฝ่าย', 'the same picture seen from the other side'), ix.numOf(reversedBits(bits))],
  ];

  return (
    <div className="mm-ich-detail">
      <div className="mm-ich-dhead">
        <HexFigure bits={bits} mark={marked}></HexFigure>
        <div>
          <h4>{h.n}. {h.cn} <em className="dim">{h.pinyin}</em> · {en ? h.en : h.th}</h4>
          <p className="dim">{h.structure}</p>
        </div>
      </div>

      <div className="mm-ich-tris">
        <TrigramCard t={ix.upper(n)} place={t('บน (นอก)', 'upper (outer)')} en={en}></TrigramCard>
        <TrigramCard t={ix.lower(n)} place={t('ล่าง (ใน)', 'lower (inner)')} en={en}></TrigramCard>
      </div>

      <p className="mm-ich-mean">{m.desc}</p>
      {m.judgment && <p className="mm-ich-quote"><b className="mono dim">卦辭 {t('คำตัดสิน', 'Judgment')}</b> {m.judgment}</p>}
      {m.image && <p className="mm-ich-quote img"><b className="mono dim">大象 {t('ภาพลักษณ์', 'Image')}</b> {m.image}</p>}

      {pal && (
        <p className="mm-ich-pal mono dim">
          京房八宮 · {pal.palace.cn}{t('กง', ' palace')} {pal.rank} —
          {t(' เส้นตัวเรา 世 อยู่ที่ ', ' the self line 世 sits at ')}<b>{pal.world}</b>
          {t(' · เส้นคู่กรณี 應 ที่ ', ' · the other line 應 at ')}<b>{pal.other}</b>
        </p>
      )}

      <div className="mm-ich-rel">
        {rel.map(([label, why, num]) => {
          const r = ix.byNum.get(num)!;
          return (
            <button key={label} className="mm-ich-relcard" onClick={() => onGo(num)}>
              <span className="mm-ich-relsym">{r.sym}</span>
              <b>{label}</b>
              <small>{r.n}. {r.cn} · {en ? r.en : r.th}</small>
              <em className="dim">{why}</em>
            </button>
          );
        })}
      </div>

      <ol className="mm-ich-lines2">
        {h.lines.map((text, i) => {
          const role = roles[i];
          const on = marked.includes(i + 1);
          const moved = ix.numOf(changedBits(bits, [i + 1]));
          const mh = ix.byNum.get(moved)!;
          return (
            <li key={i} className={on ? 'on' : ''}>
              <div className="mm-ich-lhead">
                <b className="mono">{lineName(bits, i + 1)}</b>
                <span className="dim mono">{t('เส้นที่', 'line')} {i + 1}</span>
                {on && <span className="mm-ich-yours">{t('เส้นของคุณ', 'yours')}</span>}
              </div>
              <p>{text}</p>
              <RoleChips role={role} en={en}></RoleChips>
              <p className="mm-ich-moved mono dim">
                {t('เส้นนี้เคลื่อน →', 'if this line moves →')} <b onClick={() => onGo(moved)}>{mh.sym} {mh.n}. {mh.cn} {en ? mh.en : mh.th}</b>
              </p>
            </li>
          );
        })}
      </ol>

      <p className="mm-ich-hd">
        <b className="mono dim">Human Design</b> Gate {h.hd.gate} · {h.hd.name} — {h.hd.center} · {h.hd.channel}
        <span className="dim"> · {t('ประตูคู่โปรแกรม', 'programming partner')} {h.hd.partner}</span>
      </p>
      <p className="dim">{h.hd.keynote}</p>
    </div>
  );
}

// -------------------------------------------------------------- the panel

interface Activation { gate: number; line: number; sym: string; name?: string }

export function IChingPanel({ personality, design }: { personality: Activation[]; design: Activation[] }) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [data, setData] = useState<Hexagram[] | null>(null);
  const [side, setSide] = useState<'personality' | 'design' | 'both'>('personality');
  const [open, setOpen] = useState<number | null>(null);
  const [showGrid, setShowGrid] = useState(false);

  // 150 KB of hexagram text has no business in the main bundle — it loads the
  // first time someone opens this tab and never again.
  useEffect(() => {
    let alive = true;
    import('../data/iching.json').then(m => { if (alive) setData((m.default || m) as Hexagram[]); });
    return () => { alive = false; };
  }, []);

  const ix = useMemo(() => (data ? buildIndex(data) : null), [data]);
  const palaces = useMemo(() => (ix ? buildPalaces(ix) : null), [ix]);
  const problems = useMemo(() => (ix ? assertIndex(ix) : []), [ix]);

  const list = useMemo<Activation[]>(() => (
    side === 'both' ? [...personality, ...design] : side === 'personality' ? personality : design
  ), [side, personality, design]);

  /** Every line the reader actually holds, per hexagram — used to light up figures. */
  const marks = useMemo(() => {
    const m = new Map<number, number[]>();
    for (const a of [...personality, ...design]) {
      const arr = m.get(a.gate) || [];
      if (!arr.includes(a.line)) arr.push(a.line);
      m.set(a.gate, arr);
    }
    return m;
  }, [personality, design]);

  /**
   * The reader's own chart summarised in I Ching terms.
   *
   * Trigram weight counts both halves of every hexagram they hold, so a chart
   * loaded with ☵ really is a chart about crossing dangerous water. The line
   * tally is the more interesting one: Human Design reads the same six line
   * positions as its profile, so this is the two systems measuring one thing.
   */
  const summary = useMemo(() => {
    if (!ix) return null;
    const tri = new Map<string, number>();
    const byLine = [0, 0, 0, 0, 0, 0];
    let central = 0, correct = 0, answered = 0, total = 0;
    for (const a of [...personality, ...design]) {
      if (!ix.byNum.has(a.gate)) continue;
      const bits = ix.bits(a.gate);
      for (const g of [ix.upper(a.gate).glyph, ix.lower(a.gate).glyph]) tri.set(g, (tri.get(g) || 0) + 1);
      const r = lineRole(bits, a.line);
      byLine[a.line - 1]++;
      if (r.central) central++;
      if (r.correct) correct++;
      if (r.answered) answered++;
      total++;
    }
    const ranked = TRIGRAMS.map(x => ({ t: x, n: tri.get(x.glyph) || 0 })).sort((a, b) => b.n - a.n);
    const max = ranked[0]?.n || 1;
    return { ranked, max, byLine, central, correct, answered, total };
  }, [ix, personality, design]);

  if (!data || !ix || !palaces) return <p className="dim">{t('กำลังโหลดฐานข้อมูล 64 เหอซ่าง…', 'Loading the 64 hexagrams…')}</p>;
  if (problems.length) return <p className="mm-warn">{t('ฐานข้อมูลอี้จิงไม่สมบูรณ์', 'The hexagram database is incomplete')}: {problems.join('; ')}</p>;

  return (
    <div className="mm-ich">
      {/* ---- the chart, summarised ---- */}
      {summary && summary.total > 0 && (
        <div className="mm-ich-sum">
          <div className="mm-ich-sumcol">
            <b className="mono dim">{t('ตรีลักษณ์ที่หนักที่สุดในดวง', 'Heaviest trigrams in your chart')}</b>
            <div className="mm-ich-tribars">
              {summary.ranked.filter(r => r.n > 0).map(({ t: tg, n }) => (
                <div className="mm-ich-tribar" key={tg.glyph}>
                  <span className="mm-ich-tg">{tg.glyph}</span>
                  <span className="mm-ich-tn">{tg.cn} {en ? tg.en : tg.th}</span>
                  <i style={{ width: `${(n / summary.max) * 100}%` }}></i>
                  <em className="mono">{n}</em>
                </div>
              ))}
            </div>
            <p className="mm-footnote dim">{t(
              'นับตรีลักษณ์บนและล่างของทุกเหอซ่างที่คุณถือ — ตัวที่ยาวที่สุดคือบรรยากาศที่ดวงคุณวนอยู่',
              'Counts the upper and lower trigram of every hexagram you hold — the longest bar is the weather your chart keeps returning to.')}</p>
          </div>

          <div className="mm-ich-sumcol">
            <b className="mono dim">{t('เส้นที่ดาวตกบ่อยที่สุด', 'Where your planets land')}</b>
            <div className="mm-ich-linebars">
              {summary.byLine.map((n, i) => (
                <div className="mm-ich-lb" key={i}>
                  <span className="mono">{t('เส้น', 'line')} {i + 1}</span>
                  <i style={{ width: `${(n / Math.max(...summary.byLine, 1)) * 100}%` }}></i>
                  <em className="mono">{n}</em>
                </div>
              ))}
            </div>
            <div className="mm-ich-stats mono">
              <span>中 {summary.central}/{summary.total}</span>
              <span>當位 {summary.correct}/{summary.total}</span>
              <span>應 {summary.answered}/{summary.total}</span>
            </div>
            <p className="mm-footnote dim">{t(
              'Human Design อ่านตำแหน่งเส้นชุดเดียวกันนี้เป็นโปรไฟล์ — ตัวเลขนี้คือสองศาสตร์วัดของสิ่งเดียวกัน',
              'Human Design reads these same six line positions as your profile — this is the two systems measuring one thing.')}</p>
          </div>
        </div>
      )}

      {/* ---- which side of the chart ---- */}
      <div className="mm-filterrow" style={{ marginBottom: 10 }}>
        {([['personality', t('Personality (ขณะเกิด)', 'Personality')], ['design', t('Design (88 วันก่อน)', 'Design')], ['both', t('ทั้งสองฝั่ง', 'Both sides')]] as const).map(([s, label]) => (
          <button key={s} className={`mm-pill ${side === s ? 'on' : ''}`} onClick={() => setSide(s as any)}>{label}</button>
        ))}
        <button className={`mm-pill ${showGrid ? 'on' : ''}`} onClick={() => setShowGrid(v => !v)}>
          {t('ตาราง 64 เหอซ่าง', 'All 64')}
        </button>
      </div>

      {/* ---- the classical 8x8 chart ---- */}
      {showGrid && (
        <div className="mm-ich-64">
          <table className="mm-ich-table">
            <thead>
              <tr>
                <th className="dim mono">{t('ล่าง \\ บน', 'lower \\ upper')}</th>
                {TRIGRAMS.map(u => <th key={u.glyph}><span className="mm-ich-tg">{u.glyph}</span><small className="dim">{u.cn}</small></th>)}
              </tr>
            </thead>
            <tbody>
              {TRIGRAMS.map(lo => (
                <tr key={lo.glyph}>
                  <th><span className="mm-ich-tg">{lo.glyph}</span><small className="dim">{lo.cn}</small></th>
                  {TRIGRAMS.map(up => {
                    const num = ix.numOf([...lo.bits, ...up.bits]);
                    const h = ix.byNum.get(num)!;
                    const mine = marks.has(num);
                    return (
                      <td key={up.glyph}>
                        <button className={`mm-ich-cell ${mine ? 'mine' : ''} ${open === num ? 'on' : ''}`}
                          onClick={() => setOpen(open === num ? null : num)} title={`${h.n}. ${h.cn} ${en ? h.en : h.th}`}>
                          <span className="mm-ich-csym">{h.sym}</span>
                          <em className="mono">{h.n}</em>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mm-footnote dim">{t(
            'ตารางคลาสสิก — แถวคือตรีลักษณ์ล่าง คอลัมน์คือบน · ช่องที่เรืองแสงคือเหอซ่างที่ดาวของคุณตกจริง',
            'The classical chart — rows are the lower trigram, columns the upper. Glowing cells are hexagrams your planets actually landed on.')}</p>
        </div>
      )}

      {/* ---- one card per activation ---- */}
      <div className="mm-ich-grid">
        {list.map((a, i) => {
          const h = ix.byNum.get(a.gate);
          if (!h) return null;
          const bits = ix.bits(a.gate);
          const role = lineRole(bits, a.line);
          const moved = ix.numOf(changedBits(bits, [a.line]));
          const mh = ix.byNum.get(moved)!;
          return (
            <div className={`mm-ich-card ${open === a.gate ? 'open' : ''}`} key={`${a.gate}-${a.line}-${i}`}>
              <div className="mm-ich-top" onClick={() => setOpen(open === a.gate ? null : a.gate)}>
                <HexFigure bits={bits} mark={[a.line]} size="sm"></HexFigure>
                <div className="mm-ich-id">
                  <b>{h.n}. {h.cn} <em className="dim">{h.pinyin}</em></b>
                  <small className="dim">{en ? h.en : h.th}</small>
                </div>
                <span className="mm-ich-planet mono dim">{a.sym} {a.gate}.{a.line}</span>
              </div>
              {/* The line is the point: the same gate reads differently on each
                  of its six lines, and the chart says which one is yours. */}
              <p className="mm-ich-line">
                <b className="mono">{lineName(bits, a.line)}</b> — {h.lines[a.line - 1]}
              </p>
              <RoleChips role={role} en={en}></RoleChips>
              <p className="mm-ich-moved mono dim">
                {t('เส้นนี้เคลื่อน →', 'if this line moves →')} <b onClick={() => setOpen(moved)}>{mh.sym} {mh.n}. {mh.cn}</b>
              </p>
              {open === a.gate && (
                <HexDetail ix={ix} palaces={palaces} n={a.gate} marked={marks.get(a.gate) || []}
                  onGo={setOpen} en={en} t={t}></HexDetail>
              )}
            </div>
          );
        })}
      </div>

      {/* A hexagram opened from the 64-grid is usually not one of the reader's
          own, so it has no card to expand into — it gets its own panel here. */}
      {open != null && !list.some(a => a.gate === open) && (
        <div className="mm-ich-card open standalone">
          <HexDetail ix={ix} palaces={palaces} n={open} marked={marks.get(open) || []}
            onGo={setOpen} en={en} t={t}></HexDetail>
        </div>
      )}

      <p className="mm-footnote dim">{t(
        'ประตูทั้ง 64 ของ Human Design คือเหอซ่างทั้ง 64 ของอี้จิงตัวต่อตัว — ตัวหนังสือมาจากตำรา ส่วนโครงสร้าง (ตรีลักษณ์ · 互/錯/綜 · 中正應 · 八宮 · 之卦) คำนวณจากหกเส้นทั้งหมด',
        'The 64 Human Design gates are the 64 I Ching hexagrams, one to one. The words come from the source texts; the structure — trigrams, nuclear/opposite/reversed, centrality, correctness and answer, the palace, and the figure your moving line produces — is all computed from the six lines.')}</p>
    </div>
  );
}
